<template>
    <div class="py-6">
        <!-- Maintenance TOS acceptance (inline in tab) -->
        <template v-if="maintenanceData?.needsTos">
            <div class="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
                <h3 class="text-lg font-bold text-gray-900 mb-2">Terms and Conditions</h3>
                <form @submit.prevent="submitTos" class="space-y-4">
                    <div class="rounded-md border border-gray-200 bg-gray-50 p-4">
                        <TermsAndConditionsForm :terms="terms" />
                    </div>
                    <div class="flex items-center gap-2">
                        <input
                            id="maintenance-tos-agree"
                            v-model="tosForm.terms_and_conditions"
                            type="checkbox"
                            class="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        <label for="maintenance-tos-agree">
                            I agree to the Terms and Conditions for the maintenance module
                        </label>
                    </div>
                    <p v-if="tosForm.errors.terms_and_conditions" class="text-sm text-red-500">{{ tosForm.errors.terms_and_conditions }}</p>
                    <button
                        type="submit"
                        :disabled="!tosForm.terms_and_conditions"
                        class="theme_color inline-flex justify-center rounded-md border border-transparent py-2 px-4 text-sm font-medium text-white shadow-sm hover:opacity-90 focus:ring-2 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        Accept and Continue
                    </button>
                </form>
            </div>
        </template>

        <!-- No maintenance access for this property -->
        <template v-else-if="maintenanceData && !maintenanceData.hasAccess">
            <div class="rounded-lg border border-gray-200 bg-gray-50 p-6">
                <p class="text-gray-600">Maintenance is not set up for this property yet. Once your landlord configures it for you, you will be able to submit requests here. You can also ask your landlord to set it up for you.</p>
            </div>
        </template>

        <!-- Has access: list requests + New Request -->
        <template v-else-if="maintenanceData?.hasAccess">
            <div class="space-y-6">
                <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <p class="text-sm text-gray-600">Submit and track maintenance requests for this property.</p>
                    <Link
                        :href="route('maintenance-requests.create', maintenanceData.property_id)"
                        class="theme_color inline-flex items-center justify-center rounded-md text-white py-2 px-4 text-sm font-medium hover:opacity-90 shrink-0"
                    >
                        New Request
                    </Link>
                </div>

                <div v-if="maintenanceData.requests && maintenanceData.requests.length" class="rounded-xl border border-gray-200 bg-white overflow-hidden">
                    <ul class="divide-y divide-gray-200">
                        <li v-for="req in maintenanceData.requests" :key="req.id">
                            <Link
                                :href="route('maintenance-requests.show', [maintenanceData.property_id, req.id])"
                                class="block px-4 py-4 sm:px-6 hover:bg-gray-50 transition-colors"
                            >
                                <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                                    <div class="min-w-0 flex-1">
                                        <p class="text-sm font-medium text-gray-900 truncate">
                                            {{ req.emergency_type_name || 'Request' }} #{{ req.id }}
                                        </p>
                                        <p v-if="req.other_description" class="mt-0.5 text-sm text-gray-500 line-clamp-2">
                                            {{ req.other_description }}
                                        </p>
                                        <p class="mt-1 text-xs text-gray-400">{{ req.created_at }}</p>
                                    </div>
                                    <span
                                        :class="statusBadgeClass(req.status)"
                                        class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium shrink-0"
                                    >
                                        {{ req.status }}
                                    </span>
                                </div>
                            </Link>
                        </li>
                    </ul>
                </div>

                <div v-else class="rounded-lg border border-gray-200 bg-gray-50 p-6 text-center">
                    <p class="text-gray-500 text-sm">No maintenance requests yet.</p>
                    <Link
                        :href="route('maintenance-requests.create', maintenanceData.property_id)"
                        class="theme_color mt-3 inline-flex items-center justify-center rounded-md text-white py-2 px-4 text-sm font-medium hover:opacity-90"
                    >
                        New Request
                    </Link>
                </div>
            </div>
        </template>

        <template v-else>
            <p class="text-gray-500 text-sm">Maintenance information is not available for this property.</p>
        </template>
    </div>
</template>

<script setup>
import { computed } from 'vue'
import { Link, useForm } from '@inertiajs/vue3'
import TermsAndConditionsForm from '@/Components/TermsAndConditions/TermsAndConditionsForm.vue'

const props = defineProps({
    property: { type: Object, default: null },
    tabData: { type: Object, default: () => ({}) },
    landlord: { type: Object, default: null },
    terms: { type: String, default: null },
})

const maintenanceData = computed(() => props.tabData?.maintenance ?? null)

const tosForm = useForm({
    terms_and_conditions: false,
    intended: typeof window !== 'undefined' && window.route ? window.route('my-property', { tab: 'maintenance' }) : '',
})

function submitTos() {
    if (!tosForm.terms_and_conditions) return
    tosForm.terms_and_conditions = 1
    const postUrl = typeof window !== 'undefined' && window.route ? window.route('maintenance.update-terms-and-conditions') : ''
    if (postUrl) tosForm.post(postUrl, { preserveScroll: true })
}

function statusBadgeClass(status) {
    if (!status) return 'bg-gray-100 text-gray-800'
    const s = String(status).toLowerCase()
    if (s === 'closed') return 'bg-gray-100 text-gray-800'
    if (s === 'in progress') return 'bg-amber-100 text-amber-800'
    return 'bg-green-100 text-green-800'
}
</script>
